import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[webResizeVerticalHandle]',
    standalone: true,
})
export class VerticalResizeHandlerDirective implements OnInit, OnDestroy {
    private readonly elementRef = inject(ElementRef);
    private readonly document = inject(DOCUMENT);

    @Input() public targetElement!: HTMLElement;
    @Input() public direction: 'top' | 'bottom' = 'bottom';
    @Input() public rotation = 0;
    @Input() public minHeight = 50;

    @Output() public heightChangeStart = new EventEmitter<void>();
    @Output() public heightChange = new EventEmitter<number>();
    @Output() public heightChangeEnd = new EventEmitter<void>();

    private startSub!: Subscription;
    private dragSub = new Subscription();

    private startX = 0;
    private startY = 0;
    private startHeight = 0;

    private rAF = 0;
    private pendingEvent: MouseEvent | null = null;

    public ngOnInit(): void {
        if (!(this.targetElement instanceof HTMLElement)) {
            console.warn('webResizeVerticalHandle: targetElement is not set or invalid.');
            return;
        }

        const mousedown$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mousedown');
        const mousemove$ = fromEvent<MouseEvent>(this.document, 'mousemove');
        const mouseup$ = fromEvent<MouseEvent>(this.document, 'mouseup');

        this.startSub = mousedown$.subscribe((startEvent) => {
            startEvent.preventDefault();
            startEvent.stopPropagation();

            this.startX = startEvent.clientX;
            this.startY = startEvent.clientY;
            this.startHeight = this.targetElement.offsetHeight;

            this.heightChangeStart.emit();

            this.dragSub.unsubscribe();
            this.dragSub = new Subscription();

            this.dragSub.add(
                mousemove$.pipe(takeUntil(mouseup$)).subscribe((moveEvent) => {
                    this.pendingEvent = moveEvent;
                    if (this.rAF) return;
                    this.rAF = requestAnimationFrame(() => {
                        this.rAF = 0;
                        const ev = this.pendingEvent!;
                        this.pendingEvent = null;
                        this.handleMove(ev);
                    });
                })
            );

            this.dragSub.add(
                mouseup$.subscribe(() => {
                    this.cancelRaf();
                    this.dragSub.unsubscribe();
                    this.heightChangeEnd.emit();
                })
            );
        });
    }

    private handleMove(event: MouseEvent): void {
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;
        const theta = (this.rotation * Math.PI) / 180;
        const localDeltaY = -dx * Math.sin(theta) + dy * Math.cos(theta);
        const newHeight = Math.round(
            Math.max(
                this.minHeight,
                this.direction === 'bottom' ? this.startHeight + localDeltaY : this.startHeight - localDeltaY
            )
        );

        this.heightChange.emit(newHeight);
    }

    private cancelRaf(): void {
        if (this.rAF) {
            cancelAnimationFrame(this.rAF);
            this.rAF = 0;
            this.pendingEvent = null;
        }
    }

    public ngOnDestroy(): void {
        this.cancelRaf();
        this.startSub?.unsubscribe();
        this.dragSub.unsubscribe();
    }
}
